import { useForm } from 'react-hook-form'
import { useMemo, useEffect } from 'react'
import { StringParam, useQueryParam } from 'use-query-params'
import { type DashMessages } from 'intl/intl.types'
import { useFormErrorMessageMap } from 'utils/react-hook-form/reactHookForm.hooks'
import { useEnterpriseAccountRequestAccountSetup } from 'domains/public/components/request-account-setup/utils/enterpriseAccountRequestAccountSetup.hooks'
import { emailValidationPattern } from 'utils/validators/emailValidation'
import { isNotFoundError } from 'api/utils/apollo/errors/isError.hooks'
import { useFormReCaptchaCheckbox } from 'gin-and-tonic/containers/re-captcha-check-box/useFormReCaptchaCheckBox.hooks'

type AccountSetupFormData = {
  email: string
  reCaptchaToken: string
}

export const usePublicRequestAccountSetupForm = () => {
  const [defaultEmail, setEmail] = useQueryParam('email', StringParam)

  const defaultValues: AccountSetupFormData = {
    email: defaultEmail || '',
    reCaptchaToken: '',
  }

  const {
    requestAccountSetup,
    apiSuccess: requestAccountSetupSuccess,
    apiError: requestAccountSetupError,
    apiLoading: requestAccountSetupLoading,
  } = useEnterpriseAccountRequestAccountSetup()

  const { register, handleSubmit, errors, setValue } = useForm<AccountSetupFormData>({
    defaultValues,
  })

  const onSubmit = handleSubmit(({ email, reCaptchaToken }) => {
    requestAccountSetup({
      email: email.trim(),
      reCaptchaToken,
    })
  })

  const emailFieldRef = register({ required: true, pattern: emailValidationPattern })

  const emailErrorMessageId = useFormErrorMessageMap({
    required: 'public.requestAccountSetup.form.fields.email.error.required',
    pattern: 'public.requestAccountSetup.form.fields.email.error.pattern',
  })(errors.email)

  const formErrorMessageId: DashMessages | undefined = useMemo(() => {
    if (!requestAccountSetupError) return undefined

    if (isNotFoundError(requestAccountSetupError)) {
      return 'public.requestAccountSetup.form.error.emailNotFound'
    }

    return 'public.requestAccountSetup.form.error.common'
  }, [requestAccountSetupError])

  const { reCaptchaElement, resetReCaptcha } = useFormReCaptchaCheckbox({
    setValue,
    register,
    action: 'public-request-account-setup-re-captcha',
    error: errors.reCaptchaToken,
  })

  useEffect(() => {
    if (formErrorMessageId) resetReCaptcha()
  }, [formErrorMessageId, resetReCaptcha])

  return {
    requestAccountSetupSuccess,
    disableFormFields: requestAccountSetupLoading || requestAccountSetupSuccess,
    onSubmit,
    emailFieldRef,
    emailErrorMessageId,
    formErrorMessageId,
    _setValue: setValue,
    reCaptchaElement,
    resetReCaptcha,
  }
}
